import React, { useState, useEffect } from 'react';
import { FaChevronDown, FaChevronUp, FaSearch, FaListOl } from 'react-icons/fa';
import { toast } from 'react-toastify';

const FAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/faq`);
        if (!response.ok) {
          throw new Error('Failed to fetch FAQs');
        }

        const json = await response.json();
        
        if (json.success) {
          setFaqs(json.data);
        } else {
          throw new Error(json.message || 'Something went wrong');
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFAQs();
  }, []);

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const filteredFAQs = faqs.filter(faq => 
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Function to render formatted content with proper styling
  const renderFormattedContent = (html) => {
    return (
      <div className="ql-editor" 
           style={{
             padding: 0,
             fontSize: '1rem',
             lineHeight: '1.5',
             color: '#4b5563'
           }}>
        <div dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-gray-800 mb-4 flex items-center justify-center">
          <FaListOl className="mr-3 text-blue-600" />
          Frequently Asked Questions
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Find answers to common questions about our services. Questions are listed in numerical order for easy reference.
        </p>
      </div>

      <div className="mb-8 relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <FaSearch className="text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Search FAQs by question or answer..."
          className="w-full pl-10 pr-4 py-3 border focus:outline-none border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="space-y-4">
        {filteredFAQs.length > 0 ? (
          filteredFAQs.map((faq, index) => (
            <div 
              key={faq.id} 
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-200"
            >
              <button
                className="w-full px-6 py-5 text-left flex items-start focus:outline-none group"
                onClick={() => toggleFAQ(index)}
              >
                <div className="flex-shrink-0 bg-blue-100 text-blue-700 rounded-lg w-10 h-10 flex items-center justify-center mr-4 mt-0.5">
                  <span className="font-bold">{index + 1}</span>
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <h3 className="text-lg font-medium text-gray-800 group-hover:text-blue-600 transition-colors">
                      {faq.question}
                    </h3>
                    <span className="text-gray-500 ml-4 mt-1 flex-shrink-0">
                      {activeIndex === index ? <FaChevronUp /> : <FaChevronDown />}
                    </span>
                  </div>
                </div>
              </button>
              
              <div 
                className={`px-6 pb-5 pt-0 transition-all duration-300 ${
                  activeIndex === index ? 'block' : 'hidden'
                }`}
              >
                <div className="flex">
                  <div className="flex-shrink-0 w-10 mr-4"></div>
                  <div className="flex-1 border-l-2 border-blue-100 pl-4">
                    {renderFormattedContent(faq.answer)}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h3 className="text-xl font-medium text-gray-700 mb-2">No FAQs found</h3>
            <p className="text-gray-500">
              {searchTerm ? 'No matching results for your search' : 'No FAQs available at the moment'}
            </p>
          </div>
        )}
      </div>
      
      {/* Add Quill CSS for proper formatting */}
      <style jsx global>{`
        .ql-editor {
          padding: 0 !important;
          font-size: 1rem !important;
          line-height: 1.5 !important;
          color: #4b5563 !important;
        }
        .ql-editor ol {
          list-style-type: decimal;
          padding-left: 1.5em;
          margin-bottom: 1em;
        }
        .ql-editor ul {
          list-style-type: disc;
          padding-left: 1.5em;
          margin-bottom: 1em;
        }
        .ql-editor h1 {
          font-size: 2em;
          font-weight: bold;
          margin-bottom: 0.67em;
        }
        .ql-editor h2 {
          font-size: 1.5em;
          font-weight: bold;
          margin-bottom: 0.83em;
        }
        .ql-editor h3 {
          font-size: 1.17em;
          font-weight: bold;
          margin-bottom: 1em;
        }
        .ql-editor p {
          margin-bottom: 1em;
        }
        .ql-editor a {
          color: #3b82f6;
          text-decoration: underline;
        }
        .ql-editor strong {
          font-weight: bold;
        }
        .ql-editor em {
          font-style: italic;
        }
        .ql-editor u {
          text-decoration: underline;
        }
        .ql-editor .ql-indent-1 {
          padding-left: 3em;
        }
        .ql-editor .ql-indent-2 {
          padding-left: 6em;
        }
        .ql-editor .ql-align-center {
          text-align: center;
        }
        .ql-editor .ql-align-right {
          text-align: right;
        }
        .ql-editor .ql-align-justify {
          text-align: justify;
        }
      `}</style>
    </div>
  );
};

export default FAQ;